import React, { useCallback, useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";
import {
  CameraInvoiceLine,
  InvoiceRole,
  InvoicesResponse,
  PaymentCollectionStatus,
  getInvoices,
} from "./invoicesService";
import "./Invoices.css";

type Props = { user?: any; onSignOut?: () => void };
type View = "CURRENT" | "HISTORY";

function emailOf(user: any) {
  return (
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    ""
  );
}

function money(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function number(value: number) {
  return new Intl.NumberFormat("en-US").format(Number(value || 0));
}

function shortDate(value: string) {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
    : "—";
}

function roleCopy(role: InvoiceRole) {
  if (role === "OWNER") {
    return "Review wholesale revenue across every Integrator and camera.";
  }
  return "Review the Qumra charges for every camera owned by your Integrator account.";
}

function collectionState(line: CameraInvoiceLine, role: InvoiceRole) {
  if (line.status === "OPEN") return { label: "Estimating", className: "open" };
  const wholesale = line.wholesalePaymentStatus;
  const status: PaymentCollectionStatus | undefined = wholesale;
  if (status === "PAID") return { label: "Paid", className: "paid" };
  if (status === "PROCESSING") return { label: "Processing", className: "processing" };
  if (status === "ACTION_REQUIRED") return { label: "Action required", className: "attention" };
  if (status === "FAILED") return { label: "Payment failed", className: "failed" };
  return { label: "Finalized", className: "finalized" };
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <article className="invoice-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function UsageDetail({ line }: { line: CameraInvoiceLine }) {
  const payg = line.billingModel === "PAYG";
  const rates = line.wholesaleRates || line.retailRates;
  return (
    <div className="invoice-usage-detail">
      <div>
        <span>AI input</span>
        <strong>{number(line.usage.aiInputTokens)} tokens</strong>
        <small>
          {money(rates.aiInputPerMillionTokens, line.currency)} / 1M
        </small>
      </div>
      <div>
        <span>AI output</span>
        <strong>{number(line.usage.aiOutputTokens)} tokens</strong>
        <small>
          {money(rates.aiOutputPerMillionTokens, line.currency)} / 1M
        </small>
      </div>
      <div>
        <span>{payg ? "Metered usage" : "Included services"}</span>
        <strong>
          {payg
            ? `${line.usage.liveTransferGb.toFixed(2)} GB live`
            : line.analyticsTier === "NONE"
              ? "Recording only"
              : `${line.analyticsTier.toLowerCase()} analytics`}
        </strong>
        <small>
          {payg
            ? `${line.usage.archivePlaybackGb.toFixed(2)} GB archive · ${number(line.usage.cloudAnalyticsImages)} cloud images`
            : "Storage, viewing, and archive playback included"}
        </small>
      </div>
    </div>
  );
}

function InvoiceRows({ lines, role }: { lines: CameraInvoiceLine[]; role: InvoiceRole }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  if (!lines.length) {
    return (
      <div className="invoice-empty">
        <strong>No invoice lines yet</strong>
        <span>Assigned cameras will appear here when their billing cycle starts.</span>
      </div>
    );
  }

  return (
    <div className="invoice-table-wrap">
      <table className="invoice-table">
        <thead>
          <tr>
            {role === "OWNER" && <th>Integrator</th>}
            <th>Admin access group</th>
            <th>Camera and plan</th>
            <th>Cycle</th>
            <th>Consumption</th>
            <th className="invoice-number">Amount due to Qumra</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const key = `${line.status}-${line.invoiceId || line.cameraId}-${line.cycle.start}`;
            const open = expanded === key;
            const collection = collectionState(line, role);
            return (
              <React.Fragment key={key}>
                <tr>
                  {role === "OWNER" && (
                    <td><strong>{line.partnerName}</strong></td>
                  )}
                  <td>
                    <strong>{line.customerName}</strong>
                    {line.customerEmail && <small>{line.customerEmail}</small>}
                  </td>
                  <td>
                    <button
                      className="invoice-camera-button"
                      type="button"
                      onClick={() => setExpanded(open ? null : key)}
                      aria-expanded={open}
                    >
                      <span>{line.cameraName || `Camera ${line.cameraId}`}</span>
                      <small>{line.planName} · {line.billingModel}</small>
                    </button>
                  </td>
                  <td>
                    <span>{shortDate(line.cycle.start)}</span>
                    <small>to {shortDate(line.cycle.end)}</small>
                  </td>
                  <td>
                    <span>{number(line.usage.aiInputTokens)} in</span>
                    <small>{number(line.usage.aiOutputTokens)} output tokens</small>
                  </td>
                  <td className="invoice-number"><strong>{money(line.wholesaleCharges?.total || 0, line.currency)}</strong></td>
                  <td>
                    <span className={`invoice-status invoice-status--${collection.className}`}>
                      {collection.label}
                    </span>
                    {line.invoiceId && <small>{line.invoiceId}</small>}
                  </td>
                </tr>
                {open && (
                  <tr className="invoice-detail-row">
                    <td colSpan={role === "OWNER" ? 7 : 6}>
                      <UsageDetail line={line} />
                      <div className="invoice-calculation">
                        <span>Plan {money(line.wholesaleCharges?.basePlan || 0, line.currency)}</span>
                        <span>AI input {money(line.wholesaleCharges?.aiInput || 0, line.currency)}</span>
                        <span>AI output {money(line.wholesaleCharges?.aiOutput || 0, line.currency)}</span>
                        {line.billingModel === "PAYG" && (
                          <span>Other metered usage {money(
                            (line.wholesaleCharges?.storage || 0) +
                              (line.wholesaleCharges?.liveTransfer || 0) +
                              (line.wholesaleCharges?.archivePlayback || 0) +
                              (line.wholesaleCharges?.cloudAnalytics || 0),
                            line.currency,
                          )}</span>
                        )}
                        <strong>Total {money(line.wholesaleCharges?.total || 0, line.currency)}</strong>
                        {line.status === "FINALIZED" && (
                          <button type="button" className="invoice-print" onClick={() => window.print()}>
                            Print invoice
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Invoices({ user, onSignOut }: Props) {
  const [data, setData] = useState<InvoicesResponse | null>(null);
  const [view, setView] = useState<View>("CURRENT");
  const [partner, setPartner] = useState("ALL");
  const [customer, setCustomer] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getInvoices());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load invoices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const role = data?.role || "INTEGRATOR";
  const source = useMemo(
    () => (view === "CURRENT" ? data?.current || [] : data?.history || []),
    [data, view],
  );
  const partners = useMemo(
    () => [...new Map(source.map((line) => [line.partnerId, line.partnerName])).entries()],
    [source],
  );
  const customers = useMemo(
    () =>
      [...new Map(
        source
          .filter((line) => partner === "ALL" || line.partnerId === partner)
          .map((line) => [line.customerId, line.customerName]),
      ).entries()],
    [partner, source],
  );
  const lines = useMemo(
    () =>
      source.filter(
        (line) =>
          (partner === "ALL" || line.partnerId === partner) &&
          (customer === "ALL" || line.customerId === customer),
      ),
    [customer, partner, source],
  );

  useEffect(() => {
    setCustomer("ALL");
  }, [partner]);

  const summary = data?.summary;
  const currency = data?.currency || "USD";

  return (
    <div className="invoices-page">
      <TopBar title="Qumra" email={emailOf(user)} onSignOut={onSignOut} />
      <main className="invoices-shell">
        <header className="invoices-hero">
          <div>
            <span className="invoices-eyebrow">Billing operations</span>
            <h1>Invoices</h1>
            <p>{roleCopy(role)}</p>
          </div>
          <span className="invoices-role">{role}</span>
        </header>

        {error && (
          <div className="invoice-alert" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => void load()}>Try again</button>
          </div>
        )}

        {loading && !data ? (
          <div className="invoice-loading">Loading invoice data…</div>
        ) : data ? (
          <>
            <section className="invoice-metrics" aria-label="Invoice summary">
              {role === "INTEGRATOR" ? (
                <>
                  <Metric label="Amount due to Qumra" value={money(summary?.wholesaleTotal || 0, currency)} detail="Across all cameras in your account" />
                  <Metric label="Cameras billed" value={number(summary?.cameraCount || 0)} detail="One invoice line per camera" />
                  <Metric label="AI Monitoring" value={money(summary?.wholesaleAiMonitoringTotal || 0, currency)} detail="Input and output token charges" />
                </>
              ) : (
                <>
                  <Metric label="Owner revenue" value={money(summary?.wholesaleTotal || 0, currency)} detail="Wholesale across all accounts" />
                  <Metric label="Hierarchy" value={`${number(summary?.integratorCount || 0)} / ${number(summary?.customerCount || 0)}`} detail="Integrators / Admin accounts" />
                  <Metric label="Active cameras" value={number(summary?.cameraCount || 0)} detail="Billed to Integrator accounts" />
                </>
              )}
            </section>

            <section className="invoice-workspace">
              <div className="invoice-toolbar">
                <div className="invoice-tabs" role="group" aria-label="Invoice period">
                  <button type="button" className={view === "CURRENT" ? "active" : ""} onClick={() => setView("CURRENT")}>Current cycles</button>
                  <button type="button" className={view === "HISTORY" ? "active" : ""} onClick={() => setView("HISTORY")}>Invoice history</button>
                </div>
                <div className="invoice-filters">
                  {role === "OWNER" && (
                    <label><span>Integrator</span><select value={partner} onChange={(event) => setPartner(event.target.value)}><option value="ALL">All Integrators</option>{partners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>
                  )}
                  <label><span>Admin access group</span><select value={customer} onChange={(event) => setCustomer(event.target.value)}><option value="ALL">All groups</option>{customers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>
                </div>
              </div>
              <div className="invoice-section-heading">
                <div>
                  <h2>{view === "CURRENT" ? "Open camera cycles" : "Finalized invoices"}</h2>
                  <p>{view === "CURRENT" ? "Amounts update as usage is recorded." : "Closed 30-day cycles are permanent billing snapshots."}</p>
                </div>
                <button type="button" className="invoice-refresh" onClick={() => void load()} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
              </div>
              <InvoiceRows lines={lines} role={role} />
              {view === "HISTORY" && data.historyLimited && (
                <p className="invoice-history-note">Showing the 100 most recent finalized camera invoices.</p>
              )}
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}
